import { NavigateFunction } from "react-router";
import { Dispatch } from "redux";
import { RefObject } from "react";
import { setIsCreateCertificateModalOpen } from "../../../modals/CreateCertificateModal/CreateCertificateModalSlice";
import { setSelectedEmployee as CertificateSetSelectedEmployee } from "../../../modals/CreateCertificateModal/CreateCertificateModalSlice";
import {
    setActiveTab,
    setCurrentPage,
    setSection,
} from "../EmployeeDetailSlice";
import { mapCertificatesToInfoCards } from "./mapCertificatesToInfoCards";
import { CertificateInterface } from "../../../common/interfaces/CertificateInterface";
import { EmployeeInterface } from "../../../common/interfaces/EmployeeInterface";
import { MetaInterface } from "../../../common/interfaces/MetaInterface";
import { SectionEnum } from "../enums/SectionEnum";
import { QualificationInterface } from "../../../common/interfaces/QualificationInterface";
import { InfoCardProps } from "@hatch-digital-nl/abs-storybook/legacy";
import { mapQualificationsToInfoCards } from "./mapQualificationsToInfoCards";
import { infoCardData } from "@hatch-digital-nl/abs-storybook/legacy";
import { getEmptyResult } from "./getEmptyResult";
import { getFunctionGroupLabel } from "../../../common/utils/getFunctionGroupLabel";

interface GetInfoCardDataProps {
    certificateDataIsFetching: boolean;
    isEstablishmentManager: boolean | number;
    certificates?: {
        data: CertificateInterface[];
        meta?: MetaInterface;
    };
    employeeData: EmployeeInterface;
    dispatch: Dispatch;
    navigate: NavigateFunction;
    openModalIndex: number;
    setOpenModalIndex: (index: number) => void;
    modalRef: RefObject<HTMLDivElement | null>;
    ellipsisWhiteImage: string;
    certificateList: {
        activeTab?: "active" | "expired";
        currentPage?: number;
        itemsPerPage?: number;
        itemsPerPageSelect: { value: number; activeValue: boolean }[];
    };
    isLeftTagModalOpenIndex: number;
    isRightTagModalOpenIndex: number;
    qualificationData?: {
        data: QualificationInterface[];
        meta?: MetaInterface;
    };
    qualificationDataIsFetching?: boolean;
    section: {
        name: "required" | "achieved";
        value: number;
    };
}

export const getInfoCardData = (
    props: GetInfoCardDataProps,
): infoCardData | undefined => {
    const {
        certificateDataIsFetching,
        isEstablishmentManager,
        certificates,
        employeeData,
        dispatch,
        navigate,
        openModalIndex,
        setOpenModalIndex,
        modalRef,
        ellipsisWhiteImage,
        certificateList,
        isLeftTagModalOpenIndex,
        isRightTagModalOpenIndex,
        qualificationData,
        qualificationDataIsFetching,
        section,
    } = props;

    const shouldShowCertificates =
        !certificateDataIsFetching &&
        isEstablishmentManager &&
        certificates &&
        certificates.meta;
    const shouldShowQualifications =
        qualificationData &&
        !qualificationDataIsFetching &&
        qualificationData.meta;

    if (!shouldShowCertificates || !shouldShowQualifications) {
        return undefined;
    }

    const hasFunctionGroup =
        employeeData?.function_groups &&
        employeeData.function_groups.length > 0;

    const infoCards: InfoCardProps[] = (() => {
        switch (section.name) {
            case SectionEnum.REQUIRED:
                return mapQualificationsToInfoCards(
                    qualificationData.data,
                    employeeData,
                    {
                        openIndex: openModalIndex,
                        setOpenModalIndex: setOpenModalIndex,
                        ref: modalRef,
                    },
                    dispatch,
                    isLeftTagModalOpenIndex,
                    isRightTagModalOpenIndex,
                );
            case SectionEnum.ACHIEVED:
                return mapCertificatesToInfoCards(
                    certificates.data,
                    {
                        openIndex: openModalIndex,
                        setOpenModalIndex: setOpenModalIndex,
                        ref: modalRef,
                    },
                    navigate,
                    dispatch,
                    isLeftTagModalOpenIndex,
                    isRightTagModalOpenIndex,
                    ellipsisWhiteImage,
                );
            default:
                return [];
        }
    })();

    return {
        emptyResultText: getEmptyResult({
            section,
            activeTab: certificateList.activeTab || "active",
            employeeData,
        }).text,
        onClickAddButton: () => {
            dispatch(setIsCreateCertificateModalOpen(true));
            dispatch(CertificateSetSelectedEmployee(employeeData));
        },
        showOnClickAddButton: getEmptyResult({
            section,
            activeTab: certificateList.activeTab || "active",
            employeeData,
        }).showButton,
        showTabs: section.name === SectionEnum.ACHIEVED,
        helperTextInHTML:
            section.name === SectionEnum.ACHIEVED
                ? undefined
                : {
                      __html: hasFunctionGroup
                          ? `Als <span>${getFunctionGroupLabel(employeeData?.function_groups)}</span> heeft deze medewerker nog de volgende certificaten nodig:`
                          : ``,
                  },
        onChangePage: (number: number) => {
            dispatch(setCurrentPage(number));
        },
        title:
            section.name === SectionEnum.REQUIRED
                ? "Interessante certificaten"
                : "Certificaten",
        total:
            section.name === SectionEnum.ACHIEVED
                ? certificates.meta!.total
                : qualificationData.meta!.total,
        tableMeta: {
            itemsPerPage: 5,
            total:
                section.name === SectionEnum.ACHIEVED
                    ? certificates.meta!.total
                    : qualificationData.meta!.total,
            currentPage: Number(certificateList.currentPage),
        },
        tabs: [
            {
                title: "Actief",
                amount: employeeData.certificate_totals.active_count,
                isActive: certificateList.activeTab === "active",
                onClick: () => dispatch(setActiveTab("active")),
            },
            {
                title: "Verlopen",
                amount: employeeData.certificate_totals.expired_count,
                isActive: certificateList.activeTab === "expired",
                onClick: () => dispatch(setActiveTab("expired")),
            },
        ],
        infoCards,
        activeSectionIndex: section.value,
        sections: [
            {
                title: `Interessante certificaten (${qualificationData.meta!.total})`,
                onClick: (index) => {
                    dispatch(setCurrentPage(1));
                    dispatch(
                        setSection({
                            name: SectionEnum.REQUIRED,
                            value: index,
                        }),
                    );
                },
            },
            {
                title: `Behaalde certificaten (${certificates.meta!.total})`,
                onClick: (index) => {
                    dispatch(setCurrentPage(1));
                    dispatch(
                        setSection({
                            name: SectionEnum.ACHIEVED,
                            value: index,
                        }),
                    );
                },
            },
        ],
    };
};
